/* eslint-disable react/prop-types */
import React, { useState, useMemo, useEffect } from 'react';
import { Markdown } from '@zedoc/react-markdown';
import { Random } from '@zedoc/random';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { saveAs } from 'file-saver';
import {
  DownloadIcon,
  EyeIcon,
  TrashIcon,
  XIcon,
  ExclamationCircleIcon,
  PlusIcon,
} from '@heroicons/react/outline';
import Dialog from '../../../../components/Dialog';
import FormFieldText from '../../../../components/forms/FormFieldText';
import FormFieldSearch from '../../../../components/forms/FormFieldSearch';
import FormFieldSwitch from '../../../../components/forms/FormFieldSwitch';
import { callMethod } from '../../../../common/utilsClient/ddp/actions';
import { apiZedocGetCustomPDFDummy } from '../../../../common/api/zedoc';
import { notifyError } from '../../../../utils/notify';
import Button from '../../../../components/Button';
import ActionCard from '../ActionCard';
import Tooltip from '../../../../common/components/Tooltip';
import PdfSectionList from './PdfSectionList';
import {
  REPORT_TYPE__RESPONSES_DOWNLOAD,
  REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD,
  REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD,
  REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL,
  REPORT_TYPE__GENERAL_REPORT_MAIL,
  isMailReportType,
} from '../../../../common/constants/collections/Projects';
import { QUESTION_TYPE__FREE_TEXT } from '@zedoc/questionnaire';
import FormFieldSelect from '../../../../components/forms/FormFieldSelect';
import FormFieldDate from '../../../../common/components/FormFieldDate';
import FormFieldTime from '../../../../common/components/FormFieldTime';
import MailSchedulerFields from '../MailSchedulerFields';
import MailRecipientsFields from '../MailRecipientsFields';
import { paramsToCron } from '../../../../screens/ProjectsDashboard/components/DashboardPreset/components/helpers';
import {
  getMailConfigMinimumError,
  getRecipientEmailsError,
  getRetryRowError,
  getRetryMinutesOverCapNotice,
  isValidFailureNotificationEmail,
  getPdfReportMinimumError,
  getPdfReportContentError,
  getScheduleWindowError,
  splitMailDateTime,
  combineMailDateTime,
  nowMailDateTime,
  RETRY_MINUTE_STEP,
  MAX_RETRY_MINUTES,
  MAX_NUMBER_OF_RETRY,
  getMaxRetryMinutesForSchedule,
  getCronRepeatGapMinutes,
  formatCronRepeatGap,
  clampRetryMinutes,
} from '../../../../screens/ProjectsDashboard/components/DashboardPreset/components/scheduleMailConfigErrors';
import { ContentCopy } from 'styled-icons/material';
import copyToClipboard from '../../../../common/utilsClient/copyToClipboard';
import { useProjectWizardEditMode } from '../ProjectWizardEditModeContext';

export const REPORT_PROP_OPTIONS = [
  {
    value: 'margin-bottom',
    label: 'margin-bottom',
    note: 'Number, Set the page bottom margin (default 10 mm)',
  },
  {
    value: 'margin-left',
    label: 'margin-left',
    note: 'Number, Set the page left margin (default 10 mm)',
  },
  {
    value: 'margin-right',
    label: 'margin-right',
    note: 'Number, Set the page right margin (default 10 mm)',
  },
  {
    value: 'margin-top',
    label: 'margin-top',
    note: 'Number, Set the page top margin (default 10 mm)',
  },

  {
    value: 'orientation',
    label: 'orientation',
    note: 'Portrait | Landscape (default Portrait)',
  },
  {
    value: 'page-size',
    label: 'page-size',
    note: 'Paper size: A4, Letter, … (default A4)',
  },

  {
    value: 'footer-font-name',
    label: 'footer-font-name',
    note: 'Footer font family (default Arial)',
  },
  {
    value: 'footer-font-size',
    label: 'footer-font-size',
    note: 'Number, Footer font size (default 12 pt)',
  },

  {
    value: 'footer-left',
    label: 'footer-left',
    note: 'Left-aligned footer text',
  },
  {
    value: 'footer-center',
    label: 'footer-center',
    note: 'Centered footer text',
  },
  {
    value: 'footer-right',
    label: 'footer-right',
    note: 'Right-aligned footer text',
  },

  {
    value: 'footer-line',
    label: 'footer-line',
    note: 'Number, Show line above footer (0/1, default 0)',
  },
  {
    value: 'footer-spacing',
    label: 'footer-spacing',
    note: 'Number,Gap between footer & content in mm (default 0)',
  },
  {
    value: 'javascript-delay',
    label: 'javascript-delay',
    note: 'Number, Wait some milliseconds for javascript finish (default 1000)',
  },
];

const getRetryOptionsForIndex = (
  retries,
  idx,
  maxMinutes = MAX_RETRY_MINUTES,
) => {
  const prev = idx > 0 ? Number(retries[idx - 1]) : null;
  const cap = Number.isFinite(maxMinutes) ? maxMinutes : MAX_RETRY_MINUTES;
  const options = [];
  for (
    let minutes = RETRY_MINUTE_STEP;
    minutes <= cap;
    minutes += RETRY_MINUTE_STEP
  ) {
    if (Number.isFinite(prev) && minutes <= prev) {
      continue;
    }
    options.push({
      value: String(minutes),
      label: `${minutes} min`,
    });
  }
  return options;
};

const bumpFollowingRetries = (
  retries,
  fromIdx,
  maxMinutes = MAX_RETRY_MINUTES,
) => {
  const next = [...retries];
  const cap = Number.isFinite(maxMinutes) ? maxMinutes : MAX_RETRY_MINUTES;
  for (let i = fromIdx + 1; i < next.length; i += 1) {
    const prev = Number(next[i - 1]);
    const curr = Number(next[i]);
    if (!Number.isFinite(prev)) {
      break;
    }
    if (!Number.isFinite(curr) || curr <= prev) {
      next[i] = prev + RETRY_MINUTE_STEP;
    }
    if (next[i] > cap) {
      return next.slice(0, i);
    }
  }
  return next.filter((minutes) => Number(minutes) <= cap);
};

const getRetryMinutes = (report) =>
  Array.isArray(report?.mailConfig?.retryMinutes)
    ? report.mailConfig.retryMinutes
    : [];

const snapshotMailConfig = (mailConfig) =>
  mailConfig ? JSON.parse(JSON.stringify(mailConfig)) : undefined;

const applyMailConfigFieldUpdate = (report, mailConfig, field, val) => {
  let nextVal = val;
  if (
    ['minutes', 'hours', 'daysOfMonth', 'months', 'daysOfWeek'].includes(
      field,
    ) &&
    Array.isArray(val)
  ) {
    nextVal = val.map((v) => String(v));
  }
  const updatedMailConfig = {
    ...mailConfig,
    [field]: nextVal,
  };

  if (
    [
      'interval',
      'minutes',
      'hours',
      'daysOfMonth',
      'months',
      'daysOfWeek',
    ].includes(field)
  ) {
    if (updatedMailConfig.interval) {
      updatedMailConfig.crontab = paramsToCron({
        interval: updatedMailConfig.interval,
        minutes: updatedMailConfig.minutes || [],
        hours: updatedMailConfig.hours || [],
        daysOfMonth: updatedMailConfig.daysOfMonth || [],
        months: updatedMailConfig.months || [],
        daysOfWeek: updatedMailConfig.daysOfWeek || [],
      });
    }
    const maxRetryMinutes = getMaxRetryMinutesForSchedule(updatedMailConfig);
    updatedMailConfig.retryMinutes = clampRetryMinutes(
      updatedMailConfig.retryMinutes,
      maxRetryMinutes,
    );
  }

  if (field === 'recipientType') {
    if (report.reportType === REPORT_TYPE__GENERAL_REPORT_MAIL) {
      updatedMailConfig.recipientType = 'manual';
    } else {
      updatedMailConfig.recipientType = val;
      if (val === 'variable' || val === 'assignee') {
        updatedMailConfig.useRecipientWiseDataFilter = true;
      }
      updatedMailConfig.recipient = {
        ...(updatedMailConfig.recipient || {}),
        target: val === 'manual' ? [] : undefined,
      };
    }
  }

  if (report.reportType === REPORT_TYPE__GENERAL_REPORT_MAIL) {
    updatedMailConfig.recipientType = 'manual';
  } else if (
    report.reportType === REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL &&
    !updatedMailConfig.recipientType
  ) {
    updatedMailConfig.recipientType = 'assignee';
  }

  return updatedMailConfig;
};

export default function PdfReportsForm({
  pdfReports = [],
  projectInfo = {},
  timezone,
  onChange,
}) {
  const { isEditMode } = useProjectWizardEditMode();
  const commitPdfReports = (nextReports) => {
    if (!isEditMode) {
      return;
    }
    onChange(nextReports);
  };
  const [expandedId, setExpandedId] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [showPropHint, setShowPropHint] = useState(false);
  const [mailSetupOpen, setMailSetupOpen] = useState(false);
  const [mailSetupSubmitFailed, setMailSetupSubmitFailed] = useState(false);
  const [mailSetupDraft, setMailSetupDraft] = useState(null);
  const dispatch = useDispatch();

  /* ----------------------------- cleanup helpers ----------------------------- */
  // Clean mailConfig from reports that are not mail-related
  const cleanReportsMailConfig = (reports) => {
    return reports.map((report) => {
      const isMailType = isMailReportType(report.reportType);
      if (!isMailType && report.mailConfig) {
        const { mailConfig, ...reportWithoutMailConfig } = report;
        return reportWithoutMailConfig;
      }
      return report;
    });
  };

  /* ----------------------------- report helpers ----------------------------- */
  const getReportTypeLabel = (reportType) => {
    const reportTypeOptions = [
      {
        value: REPORT_TYPE__RESPONSES_DOWNLOAD,
        label: 'Responses Download',
      },
      {
        value: REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD,
        label: 'Alert Summary - Single alert Download',
      },
      {
        value: REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD,
        label: 'Alert Summary - Patient Download',
      },
      {
        value: REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL,
        label: 'Alert Digest Mail',
      },
      {
        value: REPORT_TYPE__GENERAL_REPORT_MAIL,
        label: 'General Report Mail',
      },
    ];
    const option = reportTypeOptions.find((opt) => opt.value === reportType);
    return option ? option.label : reportType || 'Unknown';
  };

  const addReport = () => {
    const id = Random.id();
    const newReport = {
      id,
      name: '',
      reportType: REPORT_TYPE__RESPONSES_DOWNLOAD,
      css_properties: [],
      sections: [],
      useAllMilestones: true,
      milestones: [],
      variables: [],
      published: false,
    };
    // Only add mailConfig for mail-type reports
    // REPORT_TYPE__RESPONSES_DOWNLOAD is not a mail type, so no mailConfig
    const cleanedReports = cleanReportsMailConfig([...pdfReports, newReport]);
    commitPdfReports(cleanedReports);
    setExpandedId(id);
  };

  const removeReport = (id) => {
    const filteredReports = pdfReports.filter((r) => r.id !== id);
    const cleanedReports = cleanReportsMailConfig(filteredReports);
    commitPdfReports(cleanedReports);
    if (expandedId === id) setExpandedId(null);
  };

  const previewPdf = ({ id, format = 'html' }) => {
    const report = pdfReports.find((r) => r.id === id) || null;
    dispatch(
      callMethod(apiZedocGetCustomPDFDummy, {
        format: format,
        config: report,
      }),
    )
      .then((data) => {
        if (format == 'pdf') {
          const blob = new Blob([data], {
            type: 'application/pdf',
          });
          saveAs(blob, `response-dummy-${id}.pdf`);
        } else {
          setPreviewHtml(typeof data === 'string' ? data : '');
          console.log(data);
          setPreviewOpen(true);
        }
      })
      .catch(notifyError());
  };

  const updateField = (id, field, val) => {
    const updatedReports = pdfReports.map((r) => {
      if (r.id === id) {
        const updated = { ...r, [field]: val };
        // If reportType changes, update outputType accordingly
        if (field === 'reportType') {
          const isMailType = isMailReportType(val);
          const isDownloadType = val && val.includes('download');

          if (isMailType) {
            // Ensure mailConfig exists for mail types
            if (!updated.mailConfig) {
              updated.mailConfig = {
                startDateTime: nowMailDateTime(),
              };
            }
            // For GENERAL_REPORT_MAIL, force recipientType to 'manual'
            if (val === REPORT_TYPE__GENERAL_REPORT_MAIL) {
              updated.mailConfig.recipientType = 'manual';
            } else if (
              val === REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL &&
              !updated.mailConfig.recipientType
            ) {
              updated.mailConfig.recipientType = 'assignee';
            }
            // Default to HTML for mail types if not already set to html
            if (
              !updated.mailConfig.outputType ||
              updated.mailConfig.outputType === 'pdf'
            ) {
              updated.mailConfig = {
                ...updated.mailConfig,
                outputType: 'html',
              };
            }
          } else {
            // Clear mailConfig for non-mail types (download types)
            updated.mailConfig = undefined;
          }
        }
        return updated;
      }
      return r;
    });
    const cleanedReports = cleanReportsMailConfig(updatedReports);
    commitPdfReports(cleanedReports);
  };

  const updateMailConfigField = (id, field, val) => {
    if (!isEditMode) {
      return;
    }
    if (mailSetupOpen) {
      const report = pdfReports.find((r) => r.id === id) || { id };
      setMailSetupDraft((prev) =>
        applyMailConfigFieldUpdate(report, prev || {}, field, val),
      );
      return;
    }
    const updatedReports = pdfReports.map((r) => {
      if (r.id === id) {
        return {
          ...r,
          mailConfig: applyMailConfigFieldUpdate(
            r,
            r.mailConfig || {},
            field,
            val,
          ),
        };
      }
      return r;
    });
    const cleanedReports = cleanReportsMailConfig(updatedReports);
    commitPdfReports(cleanedReports);
  };

  const updateMailDateTimePart = (id, field, part, val, { required } = {}) => {
    const mailConfig =
      (mailSetupOpen && mailSetupDraft) ||
      pdfReports.find((report) => report.id === id)?.mailConfig ||
      {};
    const current = splitMailDateTime(mailConfig[field], timezone);
    if (!required && part === 'date' && !val) {
      updateMailConfigField(id, field, null);
      return;
    }
    let nextDate = part === 'date' ? val : current.date;
    let nextTime = part === 'time' ? val : current.time;
    if (required && !nextDate) {
      const fallback = splitMailDateTime(nowMailDateTime(), timezone);
      nextDate = fallback.date;
      if (!nextTime) {
        nextTime = fallback.time;
      }
    }
    if (required && !nextTime) {
      nextTime = splitMailDateTime(nowMailDateTime(), timezone).time;
    }
    if (!required && part === 'time' && !nextDate) {
      nextDate =
        splitMailDateTime(mailConfig.startDateTime, timezone).date ||
        splitMailDateTime(nowMailDateTime(), timezone).date;
    }
    updateMailConfigField(
      id,
      field,
      combineMailDateTime(nextDate, nextTime, timezone),
    );
  };

  const currentReport = useMemo(() => {
    if (expandedId) {
      return pdfReports.find((pdfReport) => pdfReport.id === expandedId);
    }

    return null;
  }, [expandedId, pdfReports]);

  const setupMailConfig =
    mailSetupOpen && mailSetupDraft != null
      ? mailSetupDraft
      : currentReport?.mailConfig;
  const setupReport = currentReport
    ? { ...currentReport, mailConfig: setupMailConfig }
    : null;

  const mailStartParts = splitMailDateTime(
    setupMailConfig?.startDateTime,
    timezone,
  );
  const mailEndParts = splitMailDateTime(
    setupMailConfig?.endDateTime,
    timezone,
  );
  const mailWindowError = getScheduleWindowError(setupMailConfig || {});
  const mailRetryMaxMinutes = getMaxRetryMinutesForSchedule(
    setupMailConfig || {},
  );
  const mailCronGapLabel = formatCronRepeatGap(
    getCronRepeatGapMinutes(setupMailConfig || {}),
  );
  const mailRetries = getRetryMinutes(setupReport);
  const mailSetupError = getMailConfigMinimumError(setupMailConfig || {});
  const mailRecipientError = (() => {
    const recipientType = setupMailConfig?.recipientType || 'assignee';
    if (recipientType !== 'manual' && recipientType !== 'variable') {
      return null;
    }
    const target = setupMailConfig?.recipient?.target;
    const hasTarget = Array.isArray(target) ? target.length > 0 : !!target;
    if (!hasTarget) {
      return 'Email recipients are required';
    }
    return getRecipientEmailsError(setupMailConfig || {}) || null;
  })();
  const mailRetryOverCapNotice = getRetryMinutesOverCapNotice(mailRetries);
  const lastRetryMinutes = Number(mailRetries[mailRetries.length - 1]);
  const canAddRetry =
    mailRetries.length < MAX_NUMBER_OF_RETRY &&
    mailRetryMaxMinutes >= RETRY_MINUTE_STEP &&
    (!Number.isFinite(lastRetryMinutes) ||
      lastRetryMinutes + RETRY_MINUTE_STEP <= mailRetryMaxMinutes);

  // Auto-update crontab when schedule fields change for the current report
  useEffect(() => {
    if (mailSetupOpen) {
      return;
    }
    if (currentReport?.mailConfig?.interval) {
      const mailConfig = currentReport.mailConfig;
      const newCrontab = paramsToCron({
        interval: mailConfig.interval,
        minutes: mailConfig.minutes || [],
        hours: mailConfig.hours || [],
        daysOfMonth: mailConfig.daysOfMonth || [],
        months: mailConfig.months || [],
        daysOfWeek: mailConfig.daysOfWeek || [],
      });

      // Only update if crontab has changed
      if (mailConfig.crontab !== newCrontab) {
        const updatedReports = pdfReports.map((r) => {
          if (r.id === currentReport.id) {
            return {
              ...r,
              mailConfig: {
                ...r.mailConfig,
                crontab: newCrontab,
              },
            };
          }
          return r;
        });
        const cleanedReports = cleanReportsMailConfig(updatedReports);
        commitPdfReports(cleanedReports);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentReport?.id,
    currentReport?.mailConfig?.interval,
    currentReport?.mailConfig?.minutes,
    currentReport?.mailConfig?.hours,
    currentReport?.mailConfig?.daysOfMonth,
    currentReport?.mailConfig?.months,
    currentReport?.mailConfig?.daysOfWeek,
    mailSetupOpen,
  ]);

  const updateSections = (id, nextSections) => {
    const updatedReports = pdfReports.map((r) =>
      r.id === id ? { ...r, sections: nextSections } : r,
    );
    const cleanedReports = cleanReportsMailConfig(updatedReports);
    commitPdfReports(cleanedReports);
  };

  const closeMailSetup = () => {
    setMailSetupDraft(null);
    setMailSetupSubmitFailed(false);
    setMailSetupOpen(false);
  };

  // Mail Setup edits stay in mailSetupDraft until Save. Cancel just
  // drops the draft — Dialog also fires onCancel after a successful Save.
  const cancelMailSetup = () => {
    closeMailSetup();
  };

  const addRetry = () => {
    if (!setupReport) {
      return;
    }
    const retries = getRetryMinutes(setupReport);
    if (retries.length >= MAX_NUMBER_OF_RETRY) {
      return;
    }
    const maxRetryMinutes = getMaxRetryMinutesForSchedule(
      setupReport.mailConfig || {},
    );
    if (maxRetryMinutes < RETRY_MINUTE_STEP) {
      return;
    }
    const lastMinutes = Number(retries[retries.length - 1]);
    const nextMinutes = Number.isFinite(lastMinutes)
      ? lastMinutes + RETRY_MINUTE_STEP
      : RETRY_MINUTE_STEP;
    if (nextMinutes > maxRetryMinutes) {
      return;
    }
    updateMailConfigField(setupReport.id, 'retryMinutes', [
      ...retries,
      nextMinutes,
    ]);
  };

  const updateRetry = (idx, minutes) => {
    if (!setupReport) {
      return;
    }
    const retries = [...getRetryMinutes(setupReport)];
    retries[idx] = minutes;
    const maxRetryMinutes = getMaxRetryMinutesForSchedule(
      setupReport.mailConfig || {},
    );
    updateMailConfigField(
      setupReport.id,
      'retryMinutes',
      bumpFollowingRetries(retries, idx, maxRetryMinutes),
    );
  };

  const removeRetry = (idx) => {
    if (!setupReport) {
      return;
    }
    updateMailConfigField(
      setupReport.id,
      'retryMinutes',
      getRetryMinutes(setupReport).filter((_, i) => i !== idx),
    );
  };

  const updateFailureNotificationEmails = (emails) => {
    if (!setupReport) {
      return;
    }
    const valid = (Array.isArray(emails) ? emails : [])
      .map((email) => String(email || '').trim())
      .filter((email) => isValidFailureNotificationEmail(email));
    updateMailConfigField(setupReport.id, 'failureNotificationEmails', valid);
  };

  const saveMailSetup = () => {
    if (!currentReport) {
      return;
    }
    const mailConfig = setupMailConfig || {};
    const mailError = getMailConfigMinimumError(mailConfig);
    if (mailError) {
      setMailSetupSubmitFailed(true);
      return;
    }
    const updatedReports = pdfReports.map((r) =>
      r.id === currentReport.id ? { ...r, mailConfig } : r,
    );
    commitPdfReports(cleanReportsMailConfig(updatedReports));
    closeMailSetup();
  };

  // Computed once per report per render and shared by both the card-list
  // and full-editor passes below, instead of each re-walking the section
  // tree separately for the same report.
  const reportErrorsById = useMemo(() => {
    const map = {};
    pdfReports.forEach((r) => {
      const contentError = getPdfReportContentError(r);
      map[r.id] = {
        contentError,
        reportError: getPdfReportMinimumError(r, {
          withLabel: false,
          contentError,
        }),
      };
    });
    return map;
  }, [pdfReports]);

  // When recipient type is "variable", filter only email format variables.
  const emailVariableOptions = useMemo(() => {
    return (projectInfo.variables || [])
      .filter((v) => {
        // Check if variable has jsonSchema with format: 'email'
        if (v.jsonSchema?.format === 'email') {
          return true;
        }
        // Also check if jsonSchema.items?.format === 'email' for array types
        if (v.jsonSchema?.items?.format === 'email') {
          return true;
        }
        // Check nested properties for email format
        if (v.jsonSchema?.properties) {
          const props = v.jsonSchema.properties;
          return Object.values(props).some(
            (prop) => prop.format === 'email' || prop.items?.format === 'email',
          );
        }
        return false;
      })
      .map((v) => ({
        value: v.id,
        label: v.title,
      }));
  }, [projectInfo.variables]);

  const { t } = useTranslation();
  const previewActions = [
    {
      key: 'download',
      label: t('download'),
      icon: <DownloadIcon />,
      onClick: () => previewPdf({ id: currentReport.id, format: 'pdf' }),
    },
  ];

  /* --------------------------------- render -------------------------------- */
  return (
    <>
      <div className="grid grid-cols-3 w-full">
        <div className="col-span-1 overflow-y-auto pr-4">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h2 className="m-0 text-xl font-semibold">
              <Markdown source="Custom Reports" />
            </h2>
            {isEditMode && (
              <Button type="primary" onClick={addReport}>
                Add Report
              </Button>
            )}
          </div>

          {/* Report cards */}
          <div className="stack-2">
            {pdfReports.map((r, idx) => {
              const open = expandedId === r.id;
              const { reportError } = reportErrorsById[r.id] || {};

              /* --------------------------------------------------------- */

              return (
                <button
                  key={r.id}
                  type="button"
                  className={`z-pw3-collection-item p-4 bg-surface border border-l-4 rounded-md transition-colors ${
                    reportError
                      ? 'border-danger-500'
                      : open
                      ? ''
                      : 'border-divider'
                  } w-full flex items-center justify-between`}
                  style={
                    open && !reportError
                      ? { borderLeftColor: 'var(--color-primary)' }
                      : reportError
                      ? { borderLeftColor: 'var(--color-danger-500)' }
                      : {}
                  }
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedId(open ? null : r.id);
                  }}
                >
                  <div className="flex flex-col flex-1 min-w-0 text-left">
                    <strong className="truncate w-full">{`${idx + 1}. ${
                      r.name || 'Untitled Report'
                    }`}</strong>
                    {r.reportType && (
                      <span className="text-xs text-gray-400 mt-1 truncate w-full text-left">
                        {getReportTypeLabel(r.reportType)}
                      </span>
                    )}
                    {reportError && (
                      <span className="text-xs text-danger-500 mt-1 text-left whitespace-normal">
                        {reportError}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center ml-2 flex-shrink-0">
                    {reportError && (
                      <Tooltip title={reportError}>
                        <ExclamationCircleIcon className="w-5 h-5 text-danger-500" />
                      </Tooltip>
                    )}
                    {r.published && (
                      <span className="text-xs px-2 py-1 bg-success-500 text-white rounded whitespace-nowrap ml-2">
                        Published
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <ActionCard
          className="col-span-2 overflow-y-auto"
          title={currentReport ? currentReport?.name || 'Untitled Report' : ''}
          actions={
            currentReport && (
              <div className="flex space-x-1 items-center">
                {isEditMode && (
                  <Tooltip title="Remove">
                    <Button
                      size="medium"
                      type="tertiary"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (
                          window.confirm(
                            'Are you sure you want to delete this report?',
                          )
                        ) {
                          removeReport(currentReport.id);
                        }
                      }}
                      icon={<TrashIcon className="text-danger-500" />}
                    />
                  </Tooltip>
                )}
                <Tooltip title="Copy ID">
                  <Button
                    size="medium"
                    type="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(currentReport.id);
                    }}
                    icon={<ContentCopy />}
                  />
                </Tooltip>
                <Tooltip title="Preview">
                  <Button
                    size="medium"
                    type="ghost"
                    onClick={(e) => {
                      e.stopPropagation();
                      previewPdf({ id: currentReport.id });
                    }}
                    icon={<EyeIcon />}
                  />
                </Tooltip>

                <div className="w-px bg-divider h-4" />
                <Tooltip title="Close">
                  <Button
                    size="medium"
                    type="ghost"
                    icon={<XIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      // setExpandedId(r.id);
                      setExpandedId(null);
                    }}
                  />
                </Tooltip>
              </div>
            )
          }
        >
          {!currentReport && (
            <div className="flex flex-col gap-4 justify-center items-center h-full px-6">
              <p className="text-2xl font-semibold">Custom Reports</p>
              <p className="text-center">
                Our system supports dynamic generation of PDF reports, allowing
                users to capture and present data in a clear, structured, and
                portable format. Reports can include customizable sections such
                as headers, tables, charts, and conditional content blocks.
                Users can configure layout options like page orientation,
                margins, and typography, and can preview changes in real time
                before exporting. Each report is built with consistency in mind,
                ensuring readability across devices and printers. Advanced
                features include support for nested data, templating logic,
                embedded images, and automatic pagination with intelligent
                content splitting across pages. Once generated, reports can be
                downloaded, emailed, or stored directly in your workspace.
              </p>
            </div>
          )}
          {/* Report cards */}
          <div className="stack-2">
            {pdfReports.map((r, idx) => {
              const open = expandedId === r.id;
              const { contentError } = reportErrorsById[r.id] || {};

              /* -------- helpers for **this** report's CSS props -------- */
              const updateCssProps = (next) => {
                const updatedReports = pdfReports.map((rep) =>
                  rep.id === r.id ? { ...rep, css_properties: next } : rep,
                );
                const cleanedReports = cleanReportsMailConfig(updatedReports);
                commitPdfReports(cleanedReports);
              };

              const addCssProp = () =>
                updateCssProps([
                  ...(r.css_properties || []),
                  { key: '', value: '' },
                ]);

              const updateCssProp = (pIdx, field, val) => {
                const next = [...(r.css_properties || [])];
                next[pIdx] = { ...next[pIdx], [field]: val };
                updateCssProps(next);
              };

              const removeCssProp = (pIdx) =>
                updateCssProps(
                  (r.css_properties || []).filter((_, i) => i !== pIdx),
                );

              /* --------------------------------------------------------- */

              return (
                open && (
                  <div key={r.id} className="mt-4 space-y-4">
                    <div className="flex items-end gap-4">
                      <div className="flex-1">
                        <FormFieldText
                          name="reportName"
                          label="Report Name"
                          input={{
                            value: r.name || '',
                            onChange: (val) => updateField(r.id, 'name', val),
                          }}
                          meta={{
                            submitFailed: false,
                            error: null,
                            formulaError: null,
                          }}
                          required
                          placeholder="Enter report name"
                          disabled={!isEditMode}
                          type="text"
                        />
                      </div>
                      <div className="pb-2">
                        <FormFieldSwitch
                          name="published"
                          label="Published"
                          input={{
                            value: r.published || false,
                            onChange: (val) =>
                              updateField(r.id, 'published', val),
                          }}
                          meta={{ submitFailed: false, error: null }}
                          disabled={!isEditMode}
                        />
                      </div>
                    </div>

                    <div className="flex items-end gap-2">
                      <div className="flex-1">
                        <FormFieldSearch
                          label="Report Type"
                          input={{
                            value: r.reportType || null,
                            onChange: (val) =>
                              updateField(r.id, 'reportType', val),
                          }}
                          meta={{
                            submitFailed: false,
                            error: !r.reportType
                              ? 'Report Type is required'
                              : null,
                          }}
                          options={[
                            {
                              value: REPORT_TYPE__RESPONSES_DOWNLOAD,
                              label: 'Responses Download',
                            },
                            {
                              value:
                                REPORT_TYPE__EAPPS_ALERT_SUMMARY_SINGLE_DOWNLOAD,
                              label: 'Alert Summary - Single alert Download',
                            },
                            {
                              value:
                                REPORT_TYPE__EAPPS_ALERT_SUMMARY_PATIENT_DOWNLOAD,
                              label: 'Alert Summary - Patient Download',
                            },
                            {
                              value: REPORT_TYPE__EAPPS_ALERT_DIGEST_MAIL,
                              label: 'Alert Digest Mail',
                            },
                            {
                              value: REPORT_TYPE__GENERAL_REPORT_MAIL,
                              label: 'General Report Mail',
                            },
                          ]}
                          mode="single"
                          required
                          allowClear={false}
                          placeholder="Select a type"
                          disabled={!isEditMode}
                        />
                      </div>
                      {isMailReportType(r.reportType) && (
                        <Button
                          type="primary"
                          onClick={() => {
                            // Opening the modal must be side-effect-free — a
                            // missing startDateTime means "no start
                            // restriction" (see isWithinMailScheduleWindow),
                            // which is a valid, intentional state. Writing
                            // `now` into the draft here — even before Save —
                            // used to mean an admin who opened Mail Setup and
                            // saved an unrelated field could accidentally
                            // pin startDateTime to whatever moment they
                            // happened to open the modal, silently
                            // suppressing an already-due send scheduled
                            // before that moment. Leave it untouched; the
                            // date picker shows empty until the admin
                            // explicitly sets a start date.
                            const nextDraft =
                              snapshotMailConfig(r.mailConfig || {}) || {};
                            if (
                              r.reportType === REPORT_TYPE__GENERAL_REPORT_MAIL
                            ) {
                              nextDraft.recipientType = 'manual';
                            }
                            setMailSetupDraft(nextDraft);
                            setMailSetupOpen(true);
                          }}
                        >
                          {isEditMode ? 'Mail Setup' : 'View mail setup'}
                        </Button>
                      )}
                    </div>

                    {/* ---------- /Report  properties ---------- */}

                    {!isMailReportType(r.reportType) && (
                      <FormFieldSearch
                        label="Variables"
                        input={{
                          value: r.variables || [],
                          onChange: (val) =>
                            updateField(r.id, 'variables', val),
                        }}
                        meta={{ submitFailed: false, error: null }}
                        options={(projectInfo.variables || []).map((m) => ({
                          value: m.id,
                          label: m.title,
                        }))}
                        mode="multiple"
                        allowClear
                        placeholder="Select variables"
                        disabled={!isEditMode}
                      />
                    )}

                    <div className="flex items-end gap-4">
                      <div className="w-1/4">
                        <FormFieldSearch
                          label="Milestone Level"
                          input={{
                            value: r.useAllMilestones ? 'all' : 'selected',
                            onChange: (val) =>
                              updateField(
                                r.id,
                                'useAllMilestones',
                                val === 'all',
                              ),
                          }}
                          meta={{ submitFailed: false, error: null }}
                          options={[
                            {
                              value: 'all',
                              label: 'All',
                            },
                            {
                              value: 'selected',
                              label: 'Selected',
                            },
                          ]}
                          mode="single"
                          allowClear={false}
                          placeholder="Select level"
                          disabled={!isEditMode}
                        />
                      </div>
                      {!r.useAllMilestones && (
                        <div className="flex-1" style={{ width: '75%' }}>
                          <FormFieldSearch
                            label="Milestones"
                            input={{
                              value: r.milestones || [],
                              onChange: (val) =>
                                updateField(r.id, 'milestones', val),
                            }}
                            meta={{
                              submitFailed: !r.useAllMilestones,
                              error:
                                !r.useAllMilestones &&
                                !(r.milestones || []).length
                                  ? 'Select at least one milestone'
                                  : null,
                            }}
                            options={(projectInfo.milestones || []).map(
                              (m) => ({
                                value: m.id,
                                label: m.name,
                              }),
                            )}
                            mode="multiple"
                            allowClear
                            placeholder="Select milestones"
                            disabled={!isEditMode}
                          />
                        </div>
                      )}
                    </div>

                    {/* ---------- Report  properties ---------- */}
                    <ActionCard
                      title="CSS Properties"
                      onAdd={isEditMode ? addCssProp : undefined}
                      onShowHint={() => setShowPropHint((v) => !v)}
                    >
                      {/* pop-over with the key / note table */}
                      <Dialog
                        title="Hint"
                        visible={showPropHint}
                        onOk={() => setShowPropHint((v) => !v)}
                        onCancel={() => setShowPropHint((v) => !v)}
                      >
                        <div className="text-xs">
                          <table className="w-full mb-4">
                            <thead>
                              <tr>
                                <th className="text-left font-semibold pb-1">
                                  Key
                                </th>
                                <th className="text-left font-semibold pb-1">
                                  Note / default
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {REPORT_PROP_OPTIONS.map((opt) => (
                                <tr
                                  key={opt.value}
                                  className="border-t border-gray-600"
                                >
                                  <td className="py-1 pr-2 whitespace-nowrap">
                                    {opt.label}
                                  </td>
                                  <td className="py-1">{opt.note}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          <div className="mt-3">
                            <p className="font-semibold mb-1">
                              Footer Variable Hints:
                            </p>
                            <ul className="list-disc list-inside space-y-1">
                              <li>
                                <code>[page]</code> – current page number
                              </li>
                              <li>
                                <code>[frompage]</code> – first page number
                              </li>
                              <li>
                                <code>[topage]</code> – last page number
                              </li>
                              <li>
                                <code>[webpage]</code> – source URL
                              </li>
                              <li>
                                <code>[date]</code> – current date (local
                                format)
                              </li>
                              <li>
                                <code>[isodate]</code> – current date (ISO 8601
                                format)
                              </li>
                              <li>
                                <code>[time]</code> – current time (local
                                format)
                              </li>
                            </ul>
                          </div>
                        </div>
                      </Dialog>

                      {/* existing property editor UI */}
                      {r.css_properties?.length === 0 && (
                        <p className="text-xs mb-2">None yet.</p>
                      )}

                      {r.css_properties?.map((p, pIdx) => (
                        <div
                          key={pIdx}
                          className="flex space-x-2 items-end mb-2"
                        >
                          <div className="flex-1">
                            <FormFieldSearch
                              label="CSS Key"
                              input={{
                                value: p.key || '',
                                onChange: (val) =>
                                  updateCssProp(pIdx, 'key', val),
                              }}
                              meta={{ submitFailed: false, error: null }}
                              options={REPORT_PROP_OPTIONS}
                              mode="single"
                              allowClear
                              placeholder="Report property"
                              disabled={!isEditMode}
                            />
                          </div>

                          <div className="flex-1">
                            <FormFieldText
                              name={`report-css-value-${pIdx}`}
                              label="Value"
                              input={{
                                value: p.value || '',
                                onChange: (val) =>
                                  updateCssProp(pIdx, 'value', val),
                              }}
                              meta={{ submitFailed: false, error: null }}
                              placeholder="value"
                              type="text"
                              disabled={!isEditMode}
                            />
                          </div>

                          {isEditMode && (
                            <Button
                              type="tertiary"
                              onClick={() => removeCssProp(pIdx)}
                              icon={<TrashIcon className="text-danger-500" />}
                            />
                          )}
                        </div>
                      ))}
                    </ActionCard>

                    {/* Sections (handled by recursive component) */}
                    <PdfSectionList
                      sections={r.sections}
                      onChange={(next) => {
                        if (isEditMode) {
                          updateSections(r.id, next);
                        }
                      }}
                      reportType={r.reportType}
                      readOnly={!isEditMode}
                    />
                    {contentError && (
                      <p className="text-xs text-danger-500 mt-2">
                        {contentError}
                      </p>
                    )}
                  </div>
                )
              );
            })}
          </div>
        </ActionCard>
      </div>
      {/* ─── Preview modal ─────────────────────────────────────── */}
      <Dialog
        visible={previewOpen}
        size="xl"
        title="Report preview"
        actions={previewActions}
        onCancel={() => setPreviewOpen(false)}
      >
        {/* If the API returns raw HTML, use srcDoc.
          If you switch to a PDF object-URL later, swap to src={previewHtml}. */}
        <iframe
          srcDoc={previewHtml}
          style={{ width: '100%', height: '80vh', border: 0 }}
          sandbox="allow-scripts"
        />
      </Dialog>
      {/* ─── /Preview modal ────────────────────────────────────── */}

      {/* ─── Mail Setup modal ─────────────────────────────────────── */}
      {currentReport && (
        <Dialog
          visible={mailSetupOpen}
          size="xl"
          title={isEditMode ? 'Mail Setup' : 'View mail setup'}
          onCancel={isEditMode ? cancelMailSetup : closeMailSetup}
          footer={
            <div className="cluster-2">
              {isEditMode ? (
                <>
                  <Button onClick={cancelMailSetup}>Cancel</Button>
                  <Button type="primary" onClick={saveMailSetup}>
                    Save
                  </Button>
                </>
              ) : (
                <Button type="primary" onClick={closeMailSetup}>
                  Close
                </Button>
              )}
            </div>
          }
        >
          <div className="stack-6">
            {mailSetupSubmitFailed && mailSetupError && (
              <p className="text-danger-500 text-sm m-0">{mailSetupError}</p>
            )}
            <ActionCard title="Email Configuration">
              <div className="grid grid-cols-4 gap-4">
                <div className="col-span-3">
                  <FormFieldText
                    name="subject"
                    label="Subject"
                    input={{
                      value: setupMailConfig?.subject || '',
                      onChange: (val) =>
                        updateMailConfigField(currentReport.id, 'subject', val),
                    }}
                    meta={{
                      submitFailed: false,
                      error: null,
                      formulaError: null,
                    }}
                    placeholder="Enter email subject"
                    disabled={!isEditMode}
                    type="text"
                  />
                </div>

                <div className="col-span-1">
                  <FormFieldSearch
                    label="Output Type"
                    input={{
                      value: setupMailConfig?.outputType || 'html',
                      onChange: (val) =>
                        updateMailConfigField(
                          currentReport.id,
                          'outputType',
                          val,
                        ),
                    }}
                    meta={{
                      submitFailed: false,
                      error: null,
                    }}
                    options={[
                      {
                        value: 'pdf',
                        label: 'PDF',
                      },
                      {
                        value: 'html',
                        label: 'HTML',
                      },
                    ]}
                    mode="single"
                    allowClear={false}
                    placeholder="Select output type"
                    disabled={!isEditMode}
                  />
                </div>
              </div>

              <div className="mt-4">
                <FormFieldText
                  name="body"
                  label="Body"
                  input={{
                    value: setupMailConfig?.body || '',
                    onChange: (val) =>
                      updateMailConfigField(currentReport.id, 'body', val),
                  }}
                  meta={{
                    submitFailed: false,
                    error: null,
                    formulaError: null,
                  }}
                  placeholder="Enter email body"
                  disabled={!isEditMode}
                  type={QUESTION_TYPE__FREE_TEXT}
                />
              </div>
            </ActionCard>

            <ActionCard title="Delivery Schedule">
              <div className="stack-4">
                <p className="font-medium text-sm m-0">Start Date Time</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormFieldDate
                    label="Date"
                    input={{
                      value: mailStartParts.date,
                      onChange: (val) =>
                        updateMailDateTimePart(
                          currentReport.id,
                          'startDateTime',
                          'date',
                          val,
                        ),
                    }}
                    meta={{
                      submitFailed: mailSetupSubmitFailed,
                      error: null,
                    }}
                    disabled={!isEditMode}
                  />
                  <FormFieldTime
                    label="Time"
                    input={{
                      value: mailStartParts.time,
                      onChange: (val) =>
                        updateMailDateTimePart(
                          currentReport.id,
                          'startDateTime',
                          'time',
                          val,
                        ),
                    }}
                    meta={{
                      submitFailed: mailSetupSubmitFailed,
                      error: null,
                    }}
                    disabled={!isEditMode}
                  />
                </div>
                <p className="font-medium text-sm m-0">End Date Time</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormFieldDate
                    label="Date"
                    input={{
                      value: mailEndParts.date,
                      onChange: (val) =>
                        updateMailDateTimePart(
                          currentReport.id,
                          'endDateTime',
                          'date',
                          val,
                        ),
                    }}
                    meta={{
                      submitFailed: mailSetupSubmitFailed || !!mailWindowError,
                      error: mailWindowError || null,
                    }}
                    min={mailStartParts.date || undefined}
                    disabled={!isEditMode}
                  />
                  <FormFieldTime
                    label="Time"
                    input={{
                      value: mailEndParts.time,
                      onChange: (val) =>
                        updateMailDateTimePart(
                          currentReport.id,
                          'endDateTime',
                          'time',
                          val,
                        ),
                    }}
                    meta={{
                      submitFailed: mailSetupSubmitFailed || !!mailWindowError,
                      error: null,
                    }}
                    disabled={!isEditMode}
                  />
                </div>
                <p className="text-xs text-gray-500 m-0">
                  If set, mail sends only between the start and end date time.
                  Leave either blank for no start or end limit.
                </p>
                <MailSchedulerFields
                  interval={setupMailConfig?.interval}
                  months={setupMailConfig?.months}
                  daysOfMonth={setupMailConfig?.daysOfMonth}
                  daysOfWeek={setupMailConfig?.daysOfWeek}
                  hours={setupMailConfig?.hours}
                  minutes={setupMailConfig?.minutes}
                  onIntervalChange={(val) =>
                    updateMailConfigField(currentReport.id, 'interval', val)
                  }
                  onMonthsChange={(val) =>
                    updateMailConfigField(currentReport.id, 'months', val)
                  }
                  onDaysOfMonthChange={(val) =>
                    updateMailConfigField(currentReport.id, 'daysOfMonth', val)
                  }
                  onDaysOfWeekChange={(val) =>
                    updateMailConfigField(currentReport.id, 'daysOfWeek', val)
                  }
                  onHoursChange={(val) =>
                    updateMailConfigField(currentReport.id, 'hours', val)
                  }
                  onMinutesChange={(val) =>
                    updateMailConfigField(currentReport.id, 'minutes', val)
                  }
                  submitFailed={mailSetupSubmitFailed}
                  showRecipients={false}
                  showTimezone={false}
                  disabled={!isEditMode}
                />
              </div>
            </ActionCard>

            <ActionCard title="Recipients">
              <div className="stack-4">
                <MailRecipientsFields
                  recipientType={setupMailConfig?.recipientType || 'assignee'}
                  variableOptions={emailVariableOptions}
                  onRecipientTypeChange={(val) =>
                    updateMailConfigField(
                      currentReport.id,
                      'recipientType',
                      val,
                    )
                  }
                  recipientTarget={setupMailConfig?.recipient?.target || []}
                  onRecipientTargetChange={(val) => {
                    const mailConfig = setupMailConfig || {};
                    const updatedRecipient = {
                      ...(mailConfig.recipient || {}),
                      target: val,
                    };
                    updateMailConfigField(
                      currentReport.id,
                      'recipient',
                      updatedRecipient,
                    );
                  }}
                  showCc={false}
                  showBcc={false}
                  showRecipientType={
                    currentReport.reportType !==
                    REPORT_TYPE__GENERAL_REPORT_MAIL
                  }
                  disabled={!isEditMode}
                  submitFailed={mailSetupSubmitFailed}
                  error={mailSetupSubmitFailed ? mailRecipientError : null}
                  isValidNewOption={isValidFailureNotificationEmail}
                />
                {/* Only show "Filter data by recipient" toggle for manual recipient type */}
                {/* Show info message for variable/assignee types */}
                {(setupMailConfig?.recipientType || 'assignee') ===
                  'variable' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Note: Reports are automatically filtered per recipient using
                    their email address.
                  </p>
                )}
                {(setupMailConfig?.recipientType || 'assignee') ===
                  'assignee' && (
                  <p className="text-xs text-gray-500 mt-1">
                    Note: Reports are automatically filtered per Assignee.
                  </p>
                )}
              </div>
            </ActionCard>

            <ActionCard
              title="Mail Retry and Failure Notifications"
              actions={
                isEditMode && canAddRetry ? (
                  <Button
                    size="medium"
                    type="primary"
                    icon={<PlusIcon />}
                    onClick={addRetry}
                    aria-label="Add retry"
                  />
                ) : null
              }
            >
              <p className="font-medium text-sm m-0">Retry Configuration</p>
              <p className="text-xs text-gray-500 m-0">
                {mailRetryMaxMinutes < RETRY_MINUTE_STEP
                  ? 'Retries are not available because this schedule repeats too often. Increase the gap between scheduled sends.'
                  : mailCronGapLabel
                  ? `Up to ${MAX_NUMBER_OF_RETRY} retries. Retries must finish before the next scheduled send (every ${mailCronGapLabel}, max ${mailRetryMaxMinutes} min).`
                  : `Up to ${MAX_NUMBER_OF_RETRY} retries. Retries must finish before the next scheduled send.`}
              </p>
              {mailRetryOverCapNotice ? (
                <p className="text-xs text-yellow-500 m-0">
                  {mailRetryOverCapNotice}
                  {isEditMode
                    ? ` Remove ${
                        mailRetries.length - MAX_NUMBER_OF_RETRY
                      } to bring this report within the limit.`
                    : ''}
                </p>
              ) : (
                mailRetries.length === MAX_NUMBER_OF_RETRY && (
                  <p className="text-xs text-gray-500 m-0">
                    {`Maximum of ${MAX_NUMBER_OF_RETRY} retries reached.`}
                  </p>
                )
              )}
              {mailRetries.length === 0 &&
                mailRetryMaxMinutes >= RETRY_MINUTE_STEP && (
                  <p className="text-xs mb-2 mt-2">
                    None yet.{' '}
                    {isEditMode ? 'Click + to add a retry delay.' : ''}
                  </p>
                )}
              {mailRetries.map((minutes, idx, retries) => {
                const rowError = getRetryRowError(retries, idx, {
                  maxMinutes: mailRetryMaxMinutes,
                });
                return (
                  <div
                    key={`retry-${idx}`}
                    className="flex space-x-2 items-end mb-2"
                  >
                    <div className="flex-1">
                      <FormFieldSelect
                        label={`Retry ${idx + 1}`}
                        options={getRetryOptionsForIndex(
                          retries,
                          idx,
                          mailRetryMaxMinutes,
                        )}
                        placeholder="Select delay"
                        input={{
                          value: String(minutes),
                          onChange: (val) => updateRetry(idx, Number(val)),
                        }}
                        meta={{
                          touched: mailSetupSubmitFailed || !!rowError,
                          submitFailed: mailSetupSubmitFailed,
                          error: rowError || null,
                        }}
                        disabled={!isEditMode}
                      />
                    </div>
                    {isEditMode && (
                      <Button
                        type="tertiary"
                        onClick={() => removeRetry(idx)}
                        icon={<TrashIcon className="text-danger-500" />}
                      />
                    )}
                  </div>
                );
              })}

              {isMailReportType(currentReport.reportType) && (
                <div className="stack-2 pt-2 border-t border-divider">
                  <p className="font-medium text-sm m-0">
                    Mail Failure Notifications
                  </p>
                  <p className="text-xs text-gray-500 m-0">
                    These recipients will be notified when this mail fails to
                    send after all configured retries have been attempted.
                  </p>
                  <FormFieldSearch
                    label="Email Address(es)"
                    mode="tags"
                    placeholder="Enter email addresses"
                    isValidNewOption={(inputValue) =>
                      isValidFailureNotificationEmail(inputValue)
                    }
                    input={{
                      value: setupMailConfig?.failureNotificationEmails || [],
                      onChange: updateFailureNotificationEmails,
                    }}
                    meta={{
                      submitFailed: false,
                      error: null,
                    }}
                    disabled={!isEditMode}
                  />
                  <p className="text-xs text-gray-500 m-0">
                    Press Enter or comma to add multiple email addresses.
                  </p>
                </div>
              )}
            </ActionCard>
          </div>
        </Dialog>
      )}
      {/* ─── /Mail Setup modal ────────────────────────────────────── */}
    </>
  );
}
